// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import RatingStars from '@/components/profile/RatingStars';

const SubmitFeedback = () => {
  const { token } = useParams<{ token: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackRequest, setFeedbackRequest] = useState<any>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');
  
  // Ratings state
  const [skillRating, setSkillRating] = useState(0);
  const [characterRating, setCharacterRating] = useState(0);
  const [workEthicRating, setWorkEthicRating] = useState(0);
  const [coachabilityRating, setCoachabilityRating] = useState(0);

  useEffect(() => {
    const fetchFeedbackRequest = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Debug logging
        console.log('Fetching feedback request with token:', token);
        setDebugInfo(`Token received: ${token}`);

        if (!token || token === 'undefined' || token === 'null') {
          throw new Error('Invalid feedback token in URL');
        }

        // Verify token and fetch feedback request
        const { data, error } = await supabase
          .from('profile_feedback_requests')
          .select('*, profiles!profile_feedback_requests_profile_id_fkey(*)')
          .eq('token', token)
          .single();

        if (error) {
          console.error('Error fetching feedback request:', error);
          setDebugInfo(prev => `${prev}\nDatabase query error: ${error.message}`);
          throw new Error('Invalid or expired feedback request link');
        }

        if (!data) {
          console.error('No feedback request found for token:', token);
          setDebugInfo(prev => `${prev}\nNo feedback request found for token: ${token}`);
          throw new Error('Feedback request not found');
        }

        // Check if feedback request is already completed or declined
        if (data.status === 'completed') {
          setSubmitted(true);
        } else if (data.status === 'declined') {
          setDeclined(true);
        }

        console.log('Feedback request found:', data);
        setDebugInfo(prev => `${prev}\nFeedback request found for ${data.recipient_name}`);
        setFeedbackRequest(data);
        setProfileData(data.profiles);
        
      } catch (err) {
        console.error('Error in fetchFeedbackRequest:', err);
        setDebugInfo(prev => `${prev}\nError: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchFeedbackRequest();
    } else {
      setError('Missing token parameter');
      setDebugInfo('Missing token parameter in URL');
      setIsLoading(false);
    }
  }, [token]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (!feedbackText.trim()) {
        throw new Error('Please provide feedback text');
      }

      if (!feedbackRequest) {
        throw new Error('Feedback request data is missing');
      }

      console.log('Submitting feedback for request:', feedbackRequest.id);
      
      // Log more details about what we're trying to submit
      const feedbackData = {
        profile_id: feedbackRequest.profile_id,
        recommender_name: feedbackRequest.recipient_name,
        recommender_relation: feedbackRequest.recipient_role,
        recommendation: feedbackText,
        skill_rating: skillRating || null,
        character_rating: characterRating || null, 
        work_ethic_rating: workEthicRating || null,
        coachability_rating: coachabilityRating || null,
        request_id: feedbackRequest.id,
        visible_to_athlete: true,
        received_date: new Date().toISOString()
      };
      
      console.log('Feedback data to insert:', feedbackData);

      // Insert feedback entry
      const { data, error: insertError } = await supabase
        .from('profile_feedback')
        .insert(feedbackData)
        .select();

      if (insertError) {
        console.error('Error inserting feedback:', insertError);
        setDebugInfo(prev => `${prev}\nInsert error: ${insertError.message}\nDetails: ${JSON.stringify(insertError.details || {})}`);
        throw new Error(`Failed to save your feedback: ${insertError.message}`);
      }

      console.log('Feedback saved successfully:', data);

      // Update request status
      const { error: updateError } = await supabase
        .from('profile_feedback_requests')
        .update({ 
          status: 'completed', 
          completed_at: new Date().toISOString()
        })
        .eq('id', feedbackRequest.id);

      if (updateError) {
        console.error('Error updating request status:', updateError);
        // Continue since the feedback was saved
      }

      setSubmitted(true);
      toast({
        title: 'Feedback submitted',
        description: 'Thank you for providing your feedback',
        variant: 'default',
      });

    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setDebugInfo(prev => `${prev}\nSubmission error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      
      toast({
        title: 'Submission failed',
        description: err instanceof Error ? err.message : 'Failed to submit your feedback',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      console.log('Declining feedback request:', feedbackRequest?.id);

      // Update the feedback request status to declined
      const { error: updateError } = await supabase
        .from('profile_feedback_requests')
        .update({
          status: 'declined',
        })
        .eq('id', feedbackRequest?.id);

      if (updateError) {
        console.error('Error declining feedback request:', updateError);
        throw new Error('Failed to process your response');
      }

      setDeclined(true);
      toast({
        title: 'Request declined',
        description: 'You have declined to provide feedback',
        variant: 'default',
      });

    } catch (err) {
      console.error('Error declining feedback:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      
      toast({
        title: 'Action failed',
        description: err instanceof Error ? err.message : 'Failed to decline the request',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-10 w-10 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-red-500 flex items-center justify-center">
              <AlertCircle className="mr-2 h-6 w-6" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center">{error}</p>
            
            {/* Debug information */}
            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded border text-xs font-mono whitespace-pre-wrap">
              <strong>Debug Info:</strong><br/>
              {debugInfo}
            </div>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button variant="outline" onClick={() => window.close()}>
              Close Window
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-green-600 flex items-center justify-center">
              <CheckCircle className="mr-2 h-6 w-6" />
              Feedback Submitted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center">Thank you for providing your feedback. Your assessment has been recorded.</p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button variant="outline" onClick={() => window.close()}>
              Close Window
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center">
              <X className="mr-2 h-6 w-6" />
              Request Declined
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center">You have declined to provide feedback at this time.</p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button variant="outline" onClick={() => window.close()}>
              Close Window
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl mx-auto py-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Athlete Feedback Request</CardTitle>
          <CardDescription>
            {feedbackRequest?.recipient_name}, please provide your feedback for {profileData?.name}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {profileData && (
            <div className="flex items-center space-x-4">
              {profileData.avatar_url && (
                <img 
                  src={profileData.avatar_url} 
                  alt={`${profileData.name}'s avatar`} 
                  className="w-16 h-16 rounded-full object-cover border"
                />
              )}
              <div>
                <h3 className="text-lg font-semibold">{profileData.name}</h3>
                <p className="text-sm text-gray-500">
                  {profileData.city}, {profileData.state} • Class of {profileData.grad_year}
                </p>
              </div>
            </div>
          )}

          {feedbackRequest?.personal_message && (
            <div>
              <h4 className="text-sm font-semibold text-gray-500 mb-2">Message from athlete:</h4>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md text-sm">
                {feedbackRequest.personal_message}
              </div>
            </div>
          )}

          <Separator />
          
          <div className="space-y-2">
            <label htmlFor="feedback" className="block text-sm font-medium">
              Your Feedback:
            </label>
            <Textarea
              id="feedback"
              placeholder="Please share your assessment of this athlete's skills, character, work ethic, and potential..."
              className="min-h-[200px]"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-sm">Ratings (Optional)</h4>
            
            <div className="grid gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Athletic Skill:</span>
                <RatingStars 
                  value={skillRating} 
                  onChange={setSkillRating} 
                  size="sm" 
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Character:</span>
                <RatingStars 
                  value={characterRating} 
                  onChange={setCharacterRating} 
                  size="sm" 
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Work Ethic:</span>
                <RatingStars 
                  value={workEthicRating} 
                  onChange={setWorkEthicRating} 
                  size="sm" 
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Coachability:</span>
                <RatingStars 
                  value={coachabilityRating} 
                  onChange={setCoachabilityRating} 
                  size="sm" 
                />
              </div>
            </div>
          </div>
          
          {/* Debug information for development environments */}
          {import.meta.env.DEV && (
            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded border text-xs font-mono overflow-auto">
              <strong>Debug Info:</strong><br/>
              <pre>{debugInfo}</pre>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={handleDecline}
            disabled={isSubmitting}
          >
            Decline Request
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !feedbackText.trim()}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubmitFeedback;